/* ============================================================================
   section-bd-header.js — Sticky-intelligent + burger drawer + active link.
   Port of the prototype IIFE, namespaced bdHeader. No dependency, zero
   global pollution. Re-initializable on shopify:section:load.

   Behavior:
   - scrollY > 40       → .bd-header.bd-header--shrunk     (shadow shrinks)
   - scrollY > 150 &&
     scrolling down     → .bd-header-wrap--hidden          (slide up, fade out)
   - scrolling up OR
     scrollY < 150      → remove --hidden                  (slide back)
   - Click brand
     if href starts #   → smooth scroll top (prevent default)
   - Click burger       → toggle .bd-header-sheet[data-state="open"]
   - Sheet link click   → close sheet
   - IO on sections
     listed in hrefs    → toggle .is-active on the matching link
   ============================================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  function init(root) {
    var wrap = root.querySelector('[data-bd-header-wrap]');
    var pill = root.querySelector('[data-bd-header]');
    var sheet = root.querySelector('[data-bd-header-sheet]');
    var burgers = Array.prototype.slice.call(root.querySelectorAll('[data-bd-burger]'));
    var brand = root.querySelector('[data-bd-brand]');

    if (!pill) return;

    /* ─── Sticky-intelligent scroll behavior ──────────────────────────── */
    /* Pattern:
       - scrollY > 40   → shrunk (shadow shrink)
       - scrollY < 60   → ALWAYS visible (forced reveal near the top)
       - Scroll DOWN (any delta) AND scrollY > 150 → hide
       - Scroll UP (any delta, even 1px) → reveal IMMEDIATELY
       No threshold on the direction check so trackpad micro-scrolls
       upward still trigger the reveal. */
    var lastY = window.scrollY;
    var ticking = false;
    function update() {
      var y = window.scrollY;
      pill.classList.toggle('bd-header--shrunk', y > 40);
      if (wrap && (!sheet || sheet.dataset.state !== 'open')) {
        if (y < 60) {
          wrap.classList.remove('bd-header-wrap--hidden');
        } else if (y > lastY && y > 150) {
          wrap.classList.add('bd-header-wrap--hidden');
        } else if (y < lastY) {
          wrap.classList.remove('bd-header-wrap--hidden');
        }
      }
      lastY = y;
      ticking = false;
    }
    update();
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    /* ─── Brand smooth-scroll-to-top when href is an anchor ───────────── */
    if (brand) {
      brand.addEventListener('click', function (e) {
        var href = brand.getAttribute('href') || '';
        if (href.charAt(0) === '#') {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }

    /* ─── Inline dropdown: click on the parent link toggles the panel ──
       instead of navigating (Adrien, 2026-09-11: "Nos piluliers" jumped to
       the hub immediately). Hover still opens for mouse users; the hub
       stays reachable through the first sub-link. Closes on outside click
       or Escape. The href is kept for crawlers and the JSON-LD nav. */
    var ddItems = Array.prototype.slice.call(root.querySelectorAll('.bd-header__item--has-dropdown'));
    function closeDropdowns(except) {
      ddItems.forEach(function (li) {
        if (li === except) return;
        li.classList.remove('is-open');
        var a = li.querySelector(':scope > .bd-header__link');
        if (a) a.setAttribute('aria-expanded', 'false');
      });
    }
    ddItems.forEach(function (li) {
      var link = li.querySelector(':scope > .bd-header__link');
      if (!link) return;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var open = !li.classList.contains('is-open');
        closeDropdowns(li);
        li.classList.toggle('is-open', open);
        link.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      li.addEventListener('mouseleave', function () {
        li.classList.remove('is-open');
        link.setAttribute('aria-expanded', 'false');
      });
    });
    if (ddItems.length) {
      document.addEventListener('click', function (e) {
        if (!ddItems.some(function (li) { return li.contains(e.target); })) closeDropdowns();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeDropdowns();
      });
    }

    /* ─── Burger(s) toggle the sheet + backdrop ───────────────────────── */
    /* Mobile pill bar has 2 sync'd triggers (the centered text+chevron and
       the right-side SVG). Both carry data-bd-burger. Each click toggles
       the sheet; setSheet() updates aria-expanded on ALL triggers so the
       state stays in sync visually. */
    if (burgers.length > 0 && sheet) {
      var backdrop = root.querySelector('[data-bd-header-backdrop]');
      var firstBurger = burgers[0];
      var openBurgerLabel = firstBurger.getAttribute('aria-label') || 'Open menu';
      var closeBurgerLabel = firstBurger.getAttribute('data-label-close') || 'Close menu';

      function setSheet(open) {
        sheet.dataset.state = open ? 'open' : 'closed';
        if (backdrop) backdrop.dataset.state = open ? 'open' : 'closed';
        burgers.forEach(function (b) {
          b.setAttribute('aria-expanded', open ? 'true' : 'false');
          b.setAttribute('aria-label', open ? closeBurgerLabel : openBurgerLabel);
        });
        document.documentElement.classList.toggle('bd-no-scroll', open);
        document.body.classList.toggle('bd-sheet-open', open);
      }

      burgers.forEach(function (burger) {
        burger.addEventListener('click', function () {
          setSheet(sheet.dataset.state !== 'open');
        });
      });

      // Tap the backdrop closes the sheet
      if (backdrop) {
        backdrop.addEventListener('click', function () { setSheet(false); });
      }

      // Prevent click bubble from the sheet to anything below (defense in depth)
      sheet.addEventListener('click', function (e) {
        e.stopPropagation();
      });

      // Link click: close the sheet, then let navigation proceed. For
      // placeholder hrefs (empty or "#"), prevent navigation so the page
      // doesn't appear to refresh on a no-op link.
      sheet.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function (e) {
          var href = a.getAttribute('href') || '';
          if (href === '' || href === '#') {
            e.preventDefault();
          }
          setSheet(false);
        });
      });

      // Explicit close button at the bottom of the sheet (a11y discoverable).
      var closeBtn = sheet.querySelector('[data-bd-sheet-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          setSheet(false);
        });
      }

      // ESC closes the sheet
      document.addEventListener('keyup', function (e) {
        if (e.key === 'Escape' && sheet.dataset.state === 'open') {
          setSheet(false);
          if (firstBurger) firstBurger.focus();
        }
      });
    }

    /* ─── Active link via IntersectionObserver on anchored sections ───── */
    if ('IntersectionObserver' in window) {
      var links = Array.prototype.slice.call(root.querySelectorAll('[data-bd-anchor-link]'));
      var map = {};
      links.forEach(function (l) {
        var id = l.getAttribute('data-bd-anchor-id');
        if (id) map[id] = l;
      });
      var ids = Object.keys(map);
      if (ids.length) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              links.forEach(function (l) { l.classList.remove('is-active'); });
              var link = map[entry.target.id];
              if (link) link.classList.add('is-active');
            }
          });
        }, { rootMargin: '-45% 0px -50% 0px' });
        ids.forEach(function (id) {
          var el = document.getElementById(id);
          if (el) io.observe(el);
        });
      }
    }
  }

  function bootstrap() {
    document.querySelectorAll('[data-bd-header-section]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Theme editor: re-init when this section re-renders.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-bd-header-section]') ||
               (e.target.matches && e.target.matches('[data-bd-header-section]') ? e.target : null);
    if (root) init(root);
  });
})();

/* ─── helper class injected at <html> level when the sheet is open ────── */
/* (kept in CSS for cleanliness — declared here for documentation only)
.bd-no-scroll { overflow: hidden; touch-action: none; }
*/
